import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useLocal } from './../environment';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { msalConfig } from '../../app';

export class XeroApi {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiXero.scopes.readWrite
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.baseUrl = useLocal
            ? "https://property.manager.internal.gateway/api/xero/"
            : "https://apim.ianrobertson.co.uk/api/xero/v1/";

        this.authorizeUrl = useLocal
            ? "https://property.manager.internal.gateway/api/xero-auth/"
            : "https://apim.ianrobertson.co.uk/api/xero-auth/v1/";

        this.accessTokenService = new AccessTokenHelper();
    }

    getAuthLink = id => polly()
        .logger(error => {
            console.error("An error occurred in getAuthLink", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    reject("Login interaction in-progress");
                    return;
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        const queryParams = new URLSearchParams({ id: id }).toString();

                        fetch(`${this.baseUrl}/GetXeroAuthenticationRequestForCompany?${queryParams}`, options)
                            .then(response => response.text())
                            .then(url => {
                                return resolve(url);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getContacts = id => polly()
        .logger(error => {
            console.error("An error occurred in getAuthLink", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    reject("Login interaction in-progress");
                    return;
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        fetch(`${this.baseUrl}/GetContacts/${id}`, options)
                            .then(response => response.json())
                            .then(data => {
                                let result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                return resolve(result);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getCachedContacts = id => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                // Use regular log for auth errors, error log for other errors
                if (error.status === 401 || error.status === 403) {
                    console.log("Authentication error in getCachedContacts:", error);
                } else {
                    console.error("An error occurred in getCachedContacts", error);
                }
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        reject("Login interaction in-progress");
                        return;
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}/GetCachedContacts/${id}`, options)
                                .then(response => {
                                    // First check if the response is ok (status in the range 200-299)
                                    if (!response.ok) {
                                        // Create an error object with status information
                                        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                        error.status = response.status;
                                        error.statusText = response.statusText;
                                        error.response = response;
                                        throw error; // This will be caught by the .catch() below
                                    }
                                    return response.json(); // Only try to parse JSON for successful responses
                                })
                                .then(data => {
                                    let result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    // If this is an HTTP error we created above, we can access the status
                                    if (error.status) {
                                        console.log(`API returned status code: ${error.status}`);
                                        // Store auth errors so we don't retry
                                        if (error.status === 401 || error.status === 403) {
                                            authError = error;
                                            console.log("Authorization error, not retrying");
                                        }
                                    }
                                    return reject(error);
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };

    getBankTransactionsForContact = (companyId, contactId) => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getBankTransactionsForContact", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        reject("Login interaction in-progress");
                        return;
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}/GetCachedBankTransactionsForContact/${companyId}/${contactId}`, options)
                                .then(response => {
                                    // First check if the response is ok (status in the range 200-299)
                                    if (!response.ok) {
                                        // Create an error object with status information
                                        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                        error.status = response.status;
                                        error.statusText = response.statusText;
                                        error.response = response;
                                        throw error; // This will be caught by the .catch() below
                                    }
                                    return response.json(); // Only try to parse JSON for successful responses
                                })
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    // If this is an HTTP error we created above, we can access the status
                                    if (error.status) {
                                        console.log(`API returned status code: ${error.status}`);
                                        // Store auth errors so we don't retry
                                        if (error.status === 401 || error.status === 403) {
                                            authError = error;
                                            console.log("Authorization error, not retrying");
                                        }
                                    }
                                    return reject(error);
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };


    getBankBalances = () => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getBankBalances", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        reject("Login interaction in-progress");
                        return;
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}dashboard/getCachedBankBalances`, options)
                                .then(response => {
                                    // First check if the response is ok (status in the range 200-299)
                                    if (!response.ok) {
                                        // Create an error object with status information
                                        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                        error.status = response.status;
                                        error.statusText = response.statusText;
                                        error.response = response;
                                        throw error; // This will be caught by the .catch() below
                                    }
                                    return response.json(); // Only try to parse JSON for successful responses
                                })
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    // If this is an HTTP error we created above, we can access the status
                                    if (error.status) {
                                        console.log(`API returned status code: ${error.status}`);
                                        // Store auth errors so we don't retry
                                        if (error.status === 401 || error.status === 403) {
                                            authError = error;
                                            console.log("Authorization error, not retrying");
                                        }
                                    }
                                    return reject(error);
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };

    getBalanceSnapshots = () => {
        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getBalanceSnapshots", error);
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => {
                    // If we already have an auth error from a previous attempt, reject immediately
                    if (authError) {
                        return reject(authError);
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        reject("Login interaction in-progress");
                        return;
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {
                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}dashboard/getSnapshotSummary`, options)
                                .then(response => {
                                    // First check if the response is ok (status in the range 200-299)
                                    if (!response.ok) {
                                        // Create an error object with status information
                                        const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
                                        error.status = response.status;
                                        error.statusText = response.statusText;
                                        error.response = response;
                                        throw error; // This will be caught by the .catch() below
                                    }
                                    return response.json(); // Only try to parse JSON for successful responses
                                })
                                .then(data => {
                                    const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    // If this is an HTTP error we created above, we can access the status
                                    if (error.status) {
                                        console.log(`API returned status code: ${error.status}`);
                                        // Store auth errors so we don't retry
                                        if (error.status === 401 || error.status === 403) {
                                            authError = error;
                                            console.log("Authorization error, not retrying");
                                        }
                                    }
                                    return reject(error);
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error);
                        });
                });
            });
    };
}